/* Pasteur Pharmacy accessibility maintenance layer
   Runs after third-party apps inject storefront UI and re-runs on DOM changes. */
(function () {
  'use strict';

  var lastFocusedBeforeModal = null;
  var mutationTimer = null;

  function textOf(el) {
    return (el && (el.getAttribute('aria-label') || el.textContent || el.getAttribute('title') || '') || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function ensureAccessibleNames(root) {
    var scope = root || document;

    scope.querySelectorAll('#swym-inject-header-mobile, a.swym-wishlist, button.swym-wishlist, .swym-wishlist[role="button"], .swym-inject[role="button"]').forEach(function (el) {
      if (!textOf(el)) {
        el.setAttribute('aria-label', 'Wishlist');
      }
      if (el.tagName === 'A' && !el.getAttribute('href')) {
        el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      }
    });

    scope.querySelectorAll('button, a, [role="button"]').forEach(function (el) {
      if (textOf(el)) return;
      var img = el.querySelector('img[alt]');
      if (img && img.alt.trim()) {
        el.setAttribute('aria-label', img.alt.trim());
        return;
      }
      var svg = el.querySelector('svg[aria-label], svg title');
      if (svg) {
        var label = svg.getAttribute('aria-label') || textOf(svg.querySelector('title'));
        if (label) el.setAttribute('aria-label', label);
      }
    });
  }

  function improveImages(root) {
    var scope = root || document;
    scope.querySelectorAll('img:not([alt])').forEach(function (img) {
      var label = '';
      var link = img.closest('a');
      if (link) label = textOf(link);
      if (!label) {
        var figure = img.closest('figure, picture, [class*="banner"], [class*="slide"]');
        if (figure) {
          var heading = figure.querySelector('h1, h2, h3, [role="heading"]');
          label = textOf(heading);
        }
      }
      if (!label) label = img.getAttribute('aria-label') || img.getAttribute('title') || '';
      /* Empty alt is preferable to an omitted alt when no reliable description exists.
         Informative marketing images should still receive authored alt text in Shopify. */
      img.setAttribute('alt', label || '');
    });
  }

  function ensurePageH1() {
    var main = document.querySelector('#site-main, main');
    if (!main || main.querySelector('h1')) return;

    var source = document.querySelector('[data-page-title], .page-title, .collection-title, .article-title, .product-title');
    var label = textOf(source) || document.title.replace(/\s*[|–—-]\s*Pasteur Pharmacy.*$/i, '').trim() || 'Pasteur Pharmacy';
    var h1 = document.createElement('h1');
    h1.className = 'pasteur-a11y-sr-only';
    h1.textContent = label;
    main.insertBefore(h1, main.firstChild);
  }


  function replaceHeadingTag(el, level) {
    var tag = 'h' + level;
    if (!el || el.tagName.toLowerCase() === tag) return el;

    var replacement = document.createElement(tag);
    Array.prototype.slice.call(el.attributes || []).forEach(function (attr) {
      replacement.setAttribute(attr.name, attr.value);
    });

    while (el.firstChild) replacement.appendChild(el.firstChild);
    el.parentNode.replaceChild(replacement, el);
    return replacement;
  }

  function normalizeHeadingHierarchy() {
    var main = document.querySelector('#site-main, main');
    if (!main) return;

    var headings = Array.prototype.slice.call(main.querySelectorAll('h1,h2,h3,h4,h5,h6'))
      .filter(function (el) {
        if (el.closest('[aria-hidden="true"], [data-templates], template')) return false;
        return textOf(el).length > 0;
      });

    if (!headings.length) {
      ensurePageH1();
      headings = Array.prototype.slice.call(main.querySelectorAll('h1,h2,h3,h4,h5,h6'));
    }

    if (!headings.length) return;

    var firstLevel = parseInt(headings[0].tagName.slice(1), 10);

    // If page content starts below H1, promote only the first meaningful heading to H1.
    if (firstLevel > 1 && !main.querySelector('h1')) {
      headings[0] = replaceHeadingTag(headings[0], 1);
    }

    var previousLevel = parseInt(headings[0].tagName.slice(1), 10);

    for (var i = 1; i < headings.length; i++) {
      var heading = headings[i];
      var level = parseInt(heading.tagName.slice(1), 10);

      // Prevent skipped levels such as H1 -> H3/H4/H5/H6.
      if (level > previousLevel + 1) {
        level = previousLevel + 1;
        headings[i] = replaceHeadingTag(heading, level);
      }

      previousLevel = parseInt(headings[i].tagName.slice(1), 10);
    }
  }

  function removeProblematicTitleTooltips(root) {
    var scope = root || document;
    scope.querySelectorAll('a[title], button[title], input[title], select[title], textarea[title], [role="button"][title]').forEach(function (el) {
      var title = el.getAttribute('title');
      if (!title) return;
      if (!el.getAttribute('aria-label') && !textOf(el)) el.setAttribute('aria-label', title);
      el.removeAttribute('title');
    });
  }

  function modalIsOpen(modal) {
    if (!modal) return false;
    if (modal.hidden || modal.getAttribute('aria-hidden') === 'true') return false;
    var s = window.getComputedStyle(modal);
    return s.display !== 'none' && s.visibility !== 'hidden' && parseFloat(s.opacity || '1') > 0;
  }

  function focusables(container) {
    return Array.prototype.slice.call(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null && el.getAttribute('aria-hidden') !== 'true';
    });
  }

  function setupModal(modal) {
    if (!modal || modal.dataset.pasteurA11yModalReady === 'true') return;
    modal.dataset.pasteurA11yModalReady = 'true';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    modal.addEventListener('keydown', function (e) {
      if (!modalIsOpen(modal)) return;

      if (e.key === 'Escape') {
        var close = modal.querySelector('[data-modal-close], [data-modal-1-close], .modal-close, [aria-label="Close"], [aria-label="close"]');
        if (close) close.click();
        return;
      }

      if (e.key !== 'Tab') return;
      var items = focusables(modal);
      if (!items.length) {
        e.preventDefault();
        modal.setAttribute('tabindex', '-1');
        modal.focus();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function maintainModals() {
    var modals = document.querySelectorAll('.modal, .modal-1, [role="dialog"], [aria-modal="true"]');
    var openModal = null;

    modals.forEach(function (modal) {
      setupModal(modal);
      if (modalIsOpen(modal) && !openModal) openModal = modal;
    });

    if (openModal) {
      if (!openModal.contains(document.activeElement)) {
        lastFocusedBeforeModal = document.activeElement;
        var items = focusables(openModal);
        (items[0] || openModal).focus();
      }
    } else if (lastFocusedBeforeModal && document.contains(lastFocusedBeforeModal)) {
      /* Only restore when focus has fallen back to body/document. */
      if (document.activeElement === document.body || document.activeElement === document.documentElement) {
        try { lastFocusedBeforeModal.focus(); } catch (e) {}
      }
      lastFocusedBeforeModal = null;
    }
  }

  function apply(root) {
    ensureAccessibleNames(root);
    improveImages(root);
    removeProblematicTitleTooltips(root);
    ensurePageH1();
    normalizeHeadingHierarchy();
    maintainModals();
  }

  function scheduleApply() {
    window.clearTimeout(mutationTimer);
    mutationTimer = window.setTimeout(function () { apply(document); }, 80);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { apply(document); });
  } else {
    apply(document);
  }

  var observer = new MutationObserver(scheduleApply);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open']
  });

  document.addEventListener('focusin', function () { maintainModals(); }, true);
})();
